import type { CustomerClass, CustomerDetailDto } from "@hisense/shared";
import type { FieldSpan } from "@/components/ui/FormLayout";
import type { ContactForm } from "./CustomerContactsEditor";

/*
 * Bố cục trang Sửa khách hàng (19/09/2026, cùng chuẩn sản phẩm, PI, báo giá, đơn mua và đơn hàng mẫu).
 *
 * Số liệu production 19/09/2026 — 62 khách: điều khoản thanh toán 90%, địa chỉ 89%, điện thoại 52%, nhân viên chăm sóc 3%,
 * P-Process mặc định 0%, phân loại 100% "Việt Nam". 154 người liên hệ: tên 100%, điện thoại 1 92%, email 87%, bộ phận 43%,
 * điện thoại 2 6,5%, thương hiệu 5,8%, WeChat 0%, nhóm 0%. 34/62 khách chỉ có một người liên hệ; nhiều nhất 15 (trần của hệ).
 * Người liên hệ là một BẢNG như bảng dòng hàng: bốn cột chính, bốn cột phụ ẩn khi không ai dùng, tự hiện và không ẩn được khi có.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường của khách và của người liên hệ có đúng một chỗ.
 */

/** Phần công ty và thiết lập bán hàng đang sửa. */
export interface CustomerFormDraft {
  companyName: string;
  /** Mã khách / tên viết tắt (yêu cầu v1.8 mục 2.2). */
  code: string;
  address: string;
  phone: string;
  paymentTerm: string;
  classification: CustomerClass;
  pProcessDefault: boolean;
  /** Người theo đơn (29/09/2026) — nhiều người, thay ô một người "chăm sóc khách hàng". */
  followerUserIds: string[];
  /** Khách riêng tư (30/09/2026) — ô chỉ hiện với quản trị viên; người khác không gửi trường này. */
  isPrivate: boolean;
}

/**
 * `region`: khu vực dữ liệu của người đang tạo — khách mới của đội Trung Quốc mặc định là Trung Quốc (29/09/2026).
 * `selfFollowerId`: khách mới của người "chỉ khách mình theo đơn" có sẵn tên họ (máy chủ cũng thêm); người khác để trống, như ô
 * Salesperson của Odoo.
 */
export const customerFormFrom = (existing?: CustomerDetailDto, region?: CustomerClass | null, selfFollowerId?: string | null): CustomerFormDraft => ({
  companyName: existing?.companyName ?? "",
  code: existing?.code ?? "",
  address: existing?.address ?? "",
  phone: existing?.phone ?? "",
  paymentTerm: existing?.paymentTerm ?? "",
  classification: existing?.classification ?? region ?? "vietnam",
  pProcessDefault: existing?.pProcessDefault ?? false,
  followerUserIds: existing ? existing.followers.map((f) => f.id) : selfFollowerId ? [selfFollowerId] : [],
  isPrivate: existing?.isPrivate ?? false,
});

export type CustomerGroupId = "company" | "sales";

/** Ô theo nhóm, trên lưới 12 cột: mỗi hàng kín 12 ô (tên công ty 6 + mã khách 3 + điện thoại 3; địa chỉ 12; ba ô bán hàng 4 + 4 + 4 — người theo đơn xuống dòng từng chip; P-Process 12; khách riêng tư 12, chỉ quản trị viên thấy). */
export const CUSTOMER_GROUPS: readonly { id: CustomerGroupId; fields: readonly { key: keyof CustomerFormDraft; span: FieldSpan }[] }[] = [
  { id: "company", fields: [{ key: "companyName", span: 6 }, { key: "code", span: 3 }, { key: "phone", span: 3 }, { key: "address", span: 12 }] },
  {
    id: "sales",
    fields: [
      { key: "paymentTerm", span: 4 }, { key: "classification", span: 4 }, { key: "followerUserIds", span: 4 },
      { key: "pProcessDefault", span: 12 }, { key: "isPrivate", span: 12 },
    ],
  },
];

/** Cột luôn hiện của bảng người liên hệ. */
export const CONTACT_MAIN_COLUMNS = ["name", "phone1", "email", "section"] as const;
/** Cột phụ — ít dùng (xem số liệu đầu tệp). */
export const CONTACT_SIDE_COLUMNS = ["phone2", "brandName", "wechat", "team"] as const;
export type ContactColumn = (typeof CONTACT_MAIN_COLUMNS)[number] | (typeof CONTACT_SIDE_COLUMNS)[number];

/**
 * Độ rộng cố định (rem); `null` là cột co giãn (tên, email) — chia đều phần còn lại, tối thiểu 11rem mỗi cột. Production: điện
 * thoại 1 dài tối đa 19 ký tự (phần lớn ≤ 16) — 9,5rem vừa 18 ký tự chữ đều; bộ phận tối đa 28 ký tự. Ô dài hơn cột hiện đủ khi rê chuột.
 */
export const CONTACT_COLUMN_WIDTH: Record<ContactColumn, number | null> = {
  name: null, phone1: 9.5, email: null, section: 9, phone2: 9, brandName: 8, wechat: 8, team: 8,
};
export const CONTACT_FLEX_MIN_REM = 11;
/** Cột số thứ tự và cột nút xoá. */
export const CONTACT_EDGE_COLUMNS_REM = 2 + 2;

/**
 * Cờ của người liên hệ không phải một cột: ô khoá "Chỉ quản trị viên xem" (30/09/2026) nằm trong ô tên, chỉ hiện với quản trị
 * viên — bảng không rộng thêm.
 */
export const CONTACT_FLAGS = ["isPrivate"] as const;

/** Có người liên hệ nào dùng cột phụ không — có thì cột phụ hiện và không ẩn được. */
export const contactSideColumnsUsed = (contacts: readonly ContactForm[]) =>
  contacts.some((c) => CONTACT_SIDE_COLUMNS.some((k) => c[k].trim() !== ""));
